/**
 * AppSync Events Client for Real-time Collaboration
 *
 * Provides real-time synchronization between clients using AWS AppSync Events API.
 * Supports workspace-level events for collections, requests, environments, and presence.
 */

import { events, type EventsChannel } from 'aws-amplify/data';

// Event types for different operations
export type EventType =
  | 'collection:created'
  | 'collection:updated'
  | 'collection:deleted'
  | 'request:created'
  | 'request:updated'
  | 'request:deleted'
  | 'folder:created'
  | 'folder:updated'
  | 'folder:deleted'
  | 'environment:created'
  | 'environment:updated'
  | 'environment:deleted'
  | 'presence:joined'
  | 'presence:left'
  | 'presence:editing';

// Base event payload
export interface BaseEvent {
  type: EventType;
  workspaceId: string;
  userId: string;
  timestamp: string;
  [key: string]: unknown; // Index signature for AppSync compatibility
}

// Collection events
export interface CollectionEvent extends BaseEvent {
  type: 'collection:created' | 'collection:updated' | 'collection:deleted';
  collectionId: string;
  data?: {
    name?: string;
    description?: string;
  };
}

// Request events
export interface RequestEvent extends BaseEvent {
  type: 'request:created' | 'request:updated' | 'request:deleted';
  collectionId: string;
  requestId: string;
  folderId?: string;
  data?: {
    name?: string;
    method?: string;
    url?: string;
  };
}

// Folder events
export interface FolderEvent extends BaseEvent {
  type: 'folder:created' | 'folder:updated' | 'folder:deleted';
  collectionId: string;
  folderId: string;
  data?: {
    name?: string;
    parentId?: string;
  };
}

// Environment events
export interface EnvironmentEvent extends BaseEvent {
  type: 'environment:created' | 'environment:updated' | 'environment:deleted';
  environmentId: string;
  data?: {
    name?: string;
    variables?: Array<{ key: string; value: string; isSecret: boolean; enabled: boolean }>;
  };
}

// Presence events
export interface PresenceEvent extends BaseEvent {
  type: 'presence:joined' | 'presence:left' | 'presence:editing';
  editingRequestId?: string;
  userName?: string;
  userColor?: string;
}

export type CollaborationEvent =
  | CollectionEvent
  | RequestEvent
  | FolderEvent
  | EnvironmentEvent
  | PresenceEvent;

// Subscription callback type
export type EventCallback = (event: CollaborationEvent) => void;

// Connection state
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

// Event payload type for publishing (without index signature restriction)
type EventPayload = Record<string, unknown>;

// Event client class
class EventClient {
  private workspaceChannels: Map<string, EventsChannel> = new Map();
  private presenceChannels: Map<string, EventsChannel> = new Map();
  private subscriptions: Map<string, { unsubscribe: () => void }> = new Map();
  private eventCallbacks: Set<EventCallback> = new Set();
  private connectionState: ConnectionState = 'disconnected';
  private connectionStateCallbacks: Set<(state: ConnectionState) => void> = new Set();
  private currentUserId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  /**
   * Set the current user ID for filtering out own events
   */
  setCurrentUserId(userId: string) {
    this.currentUserId = userId;
  }

  /**
   * Subscribe to workspace events
   */
  async subscribeToWorkspace(workspaceId: string): Promise<void> {
    if (this.workspaceChannels.has(workspaceId)) {
      console.log(`Already subscribed to workspace: ${workspaceId}`);
      return;
    }

    this.setConnectionState('connecting');

    try {
      // Connect to workspace channel
      const channel = await events.connect(`workspaces/${workspaceId}`);
      this.workspaceChannels.set(workspaceId, channel);

      // Subscribe to events
      const subscription = channel.subscribe({
        next: (data) => {
          this.handleIncomingEvent(data as { event: CollaborationEvent });
        },
        error: (err) => {
          console.error(`Workspace channel error for ${workspaceId}:`, err);
          this.handleConnectionError(workspaceId, 'workspace');
        },
      });

      this.subscriptions.set(`workspace:${workspaceId}`, subscription);
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;

      console.log(`Subscribed to workspace: ${workspaceId}`);
    } catch (error) {
      console.error(`Failed to subscribe to workspace ${workspaceId}:`, error);
      this.setConnectionState('disconnected');
      throw error;
    }
  }

  /**
   * Subscribe to presence channel for a workspace
   */
  async subscribeToPresence(workspaceId: string): Promise<void> {
    if (this.presenceChannels.has(workspaceId)) {
      return;
    }

    try {
      const channel = await events.connect(`presence/${workspaceId}`);
      this.presenceChannels.set(workspaceId, channel);

      const subscription = channel.subscribe({
        next: (data) => {
          this.handleIncomingEvent(data as { event: CollaborationEvent });
        },
        error: (err) => {
          console.error(`Presence channel error for ${workspaceId}:`, err);
          this.handleConnectionError(workspaceId, 'presence');
        },
      });

      this.subscriptions.set(`presence:${workspaceId}`, subscription);
      console.log(`Subscribed to presence: ${workspaceId}`);
    } catch (error) {
      console.error(`Failed to subscribe to presence ${workspaceId}:`, error);
    }
  }

  /**
   * Unsubscribe from workspace events
   */
  unsubscribeFromWorkspace(workspaceId: string): void {
    // Close workspace channel
    const workspaceChannel = this.workspaceChannels.get(workspaceId);
    if (workspaceChannel) {
      const subscription = this.subscriptions.get(`workspace:${workspaceId}`);
      subscription?.unsubscribe();
      workspaceChannel.close();
      this.workspaceChannels.delete(workspaceId);
      this.subscriptions.delete(`workspace:${workspaceId}`);
    }

    // Close presence channel
    const presenceChannel = this.presenceChannels.get(workspaceId);
    if (presenceChannel) {
      const subscription = this.subscriptions.get(`presence:${workspaceId}`);
      subscription?.unsubscribe();
      presenceChannel.close();
      this.presenceChannels.delete(workspaceId);
      this.subscriptions.delete(`presence:${workspaceId}`);
    }

    console.log(`Unsubscribed from workspace: ${workspaceId}`);

    // Update connection state if no more subscriptions
    if (this.workspaceChannels.size === 0) {
      this.setConnectionState('disconnected');
    }
  }

  /**
   * Publish an event to a workspace channel
   */
  async publishWorkspaceEvent(event: EventPayload): Promise<void> {
    const workspaceId = event.workspaceId as string;
    const eventType = event.type as string;
    const fullEvent: EventPayload = {
      ...event,
      timestamp: new Date().toISOString(),
    };

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await events.post(`workspaces/${workspaceId}`, fullEvent as any);
      console.log(`Published event to workspace ${workspaceId}:`, eventType);
    } catch (error) {
      console.error(`Failed to publish event to workspace ${workspaceId}:`, error);
      throw error;
    }
  }

  /**
   * Publish a presence event
   */
  async publishPresence(
    workspaceId: string,
    presenceType: 'joined' | 'left' | 'editing',
    data?: { editingRequestId?: string; userName?: string; userColor?: string }
  ): Promise<void> {
    if (!this.currentUserId) {
      console.warn('Cannot publish presence without user ID');
      return;
    }

    const event: EventPayload = {
      type: `presence:${presenceType}`,
      workspaceId,
      userId: this.currentUserId,
      timestamp: new Date().toISOString(),
      ...data,
    };

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await events.post(`presence/${workspaceId}`, event as any);
      console.log(`Published presence:`, presenceType);
    } catch (error) {
      console.error(`Failed to publish presence:`, error);
    }
  }

  /**
   * Register a callback for incoming events
   */
  onEvent(callback: EventCallback): () => void {
    this.eventCallbacks.add(callback);
    return () => {
      this.eventCallbacks.delete(callback);
    };
  }

  /**
   * Register a callback for connection state changes
   */
  onConnectionStateChange(callback: (state: ConnectionState) => void): () => void {
    this.connectionStateCallbacks.add(callback);
    // Immediately call with current state
    callback(this.connectionState);
    return () => {
      this.connectionStateCallbacks.delete(callback);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Disconnect all channels
   */
  disconnectAll(): void {
    // Unsubscribe from all workspaces
    for (const workspaceId of this.workspaceChannels.keys()) {
      this.unsubscribeFromWorkspace(workspaceId);
    }
    this.eventCallbacks.clear();
    this.setConnectionState('disconnected');
  }

  /**
   * Handle incoming events
   */
  private handleIncomingEvent(data: { event: CollaborationEvent }): void {
    const event = data.event;

    // Skip own events
    if (event.userId === this.currentUserId) {
      return;
    }

    console.log('Received collaboration event:', event.type, event);

    // Notify all callbacks
    for (const callback of this.eventCallbacks) {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in event callback:', error);
      }
    }
  }

  /**
   * Handle connection errors with reconnection logic
   */
  private async handleConnectionError(
    workspaceId: string,
    channelType: 'workspace' | 'presence'
  ): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`Max reconnect attempts reached for ${channelType} channel ${workspaceId}`);
      this.setConnectionState('disconnected');
      return;
    }

    this.setConnectionState('reconnecting');
    this.reconnectAttempts++;

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    console.log(`Attempting to reconnect to ${channelType} channel ${workspaceId} in ${delay}ms...`);

    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      if (channelType === 'workspace') {
        this.workspaceChannels.delete(workspaceId);
        this.subscriptions.delete(`workspace:${workspaceId}`);
        await this.subscribeToWorkspace(workspaceId);
      } else {
        this.presenceChannels.delete(workspaceId);
        this.subscriptions.delete(`presence:${workspaceId}`);
        await this.subscribeToPresence(workspaceId);
      }
    } catch (error) {
      console.error(`Reconnection failed for ${channelType} channel ${workspaceId}:`, error);
      this.handleConnectionError(workspaceId, channelType);
    }
  }

  /**
   * Update connection state and notify callbacks
   */
  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      for (const callback of this.connectionStateCallbacks) {
        callback(state);
      }
    }
  }
}

// Singleton instance
export const eventClient = new EventClient();

// Helper functions for common event publishing patterns
export const publishCollectionCreated = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  data: { name: string; description?: string }
) =>
  eventClient.publishWorkspaceEvent({
    type: 'collection:created',
    workspaceId,
    userId,
    collectionId,
    data,
  });

export const publishCollectionUpdated = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  data: { name?: string; description?: string }
) =>
  eventClient.publishWorkspaceEvent({
    type: 'collection:updated',
    workspaceId,
    userId,
    collectionId,
    data,
  });

export const publishCollectionDeleted = (
  workspaceId: string,
  userId: string,
  collectionId: string
) =>
  eventClient.publishWorkspaceEvent({
    type: 'collection:deleted',
    workspaceId,
    userId,
    collectionId,
  });

export const publishRequestCreated = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  requestId: string,
  data: { name: string; method: string; url?: string },
  folderId?: string
) =>
  eventClient.publishWorkspaceEvent({
    type: 'request:created',
    workspaceId,
    userId,
    collectionId,
    requestId,
    folderId,
    data,
  });

export const publishRequestUpdated = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  requestId: string,
  data: { name?: string; method?: string; url?: string },
  folderId?: string
) =>
  eventClient.publishWorkspaceEvent({
    type: 'request:updated',
    workspaceId,
    userId,
    collectionId,
    requestId,
    folderId,
    data,
  });

export const publishRequestDeleted = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  requestId: string,
  folderId?: string
) =>
  eventClient.publishWorkspaceEvent({
    type: 'request:deleted',
    workspaceId,
    userId,
    collectionId,
    requestId,
    folderId,
  });

export const publishFolderCreated = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  folderId: string,
  data: { name: string; parentId?: string }
) =>
  eventClient.publishWorkspaceEvent({
    type: 'folder:created',
    workspaceId,
    userId,
    collectionId,
    folderId,
    data,
  });

export const publishFolderUpdated = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  folderId: string,
  data: { name?: string; parentId?: string }
) =>
  eventClient.publishWorkspaceEvent({
    type: 'folder:updated',
    workspaceId,
    userId,
    collectionId,
    folderId,
    data,
  });

export const publishFolderDeleted = (
  workspaceId: string,
  userId: string,
  collectionId: string,
  folderId: string
) =>
  eventClient.publishWorkspaceEvent({
    type: 'folder:deleted',
    workspaceId,
    userId,
    collectionId,
    folderId,
  });

export const publishEnvironmentCreated = (
  workspaceId: string,
  userId: string,
  environmentId: string,
  data: { name: string }
) =>
  eventClient.publishWorkspaceEvent({
    type: 'environment:created',
    workspaceId,
    userId,
    environmentId,
    data,
  });

export const publishEnvironmentUpdated = (
  workspaceId: string,
  userId: string,
  environmentId: string,
  data: { name?: string; variables?: Array<{ key: string; value: string; isSecret: boolean; enabled: boolean }> }
) =>
  eventClient.publishWorkspaceEvent({
    type: 'environment:updated',
    workspaceId,
    userId,
    environmentId,
    data,
  });

export const publishEnvironmentDeleted = (
  workspaceId: string,
  userId: string,
  environmentId: string
) =>
  eventClient.publishWorkspaceEvent({
    type: 'environment:deleted',
    workspaceId,
    userId,
    environmentId,
  });
